import type {
    CommerceEventUnsubscribe,
    CommerceOperationOptions,
    CommerceOrder,
    CommerceOrderChangeReason,
    CommerceOrderChangedEvent,
    CommerceOrderChangedListener,
    CommerceOrderDomain,
    CommerceOrderWaitOptions,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceError } from "../errors/commerce-error.js";
import { CommerceOrderRepository } from "./commerce-order-repository.js";

export class CommerceOrderService implements CommerceOrderDomain {
    private readonly listeners = new DisposableListenerRegistry<CommerceOrderChangedEvent>();
    private activeWait: AbortController | undefined;
    public constructor(
        private readonly repository: CommerceOrderRepository,
        private readonly assertAvailable: () => void,
    ) {}
    public async get(
        orderId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceOrder> {
        return this.accept(
            await this.repository.get(this.requireOrderId(orderId), options),
            "loaded",
        );
    }
    public async refresh(
        orderId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceOrder> {
        return this.accept(
            await this.repository.refresh(this.requireOrderId(orderId), options),
            "updated",
        );
    }
    public async waitForCompletion(
        orderId: string,
        options: CommerceOrderWaitOptions = {},
    ): Promise<CommerceOrder> {
        this.assertAvailable();
        this.cancelPolling();
        const controller = new AbortController();
        this.activeWait = controller;
        const interval = options.intervalMilliseconds ?? 750;
        const timeout = options.timeoutMilliseconds ?? 30000;
        const started = Date.now();
        const externalAbort = () => controller.abort();
        options.signal?.addEventListener("abort", externalAbort, {
            once: true,
        });
        try {
            while (true) {
                const order = await this.refresh(orderId, {
                    signal: controller.signal,
                });
                if (order.status === "completed") return this.accept(order, "completed");
                if (order.status === "failed" || order.status === "cancelled")
                    return this.accept(order, "failed");
                if (Date.now() - started >= timeout)
                    throw new CommerceError(
                        "order-completion-timeout",
                        "The Order did not complete within the configured timeout.",
                        { orderId },
                    );
                await delay(interval, controller.signal);
            }
        } finally {
            options.signal?.removeEventListener("abort", externalAbort);
            if (this.activeWait === controller) this.activeWait = undefined;
        }
    }
    public cancelPolling(): void {
        this.activeWait?.abort();
        this.activeWait = undefined;
    }
    public onChanged(listener: CommerceOrderChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }
    public dispose(): void {
        this.cancelPolling();
        this.listeners.release();
    }
    private accept(order: CommerceOrder, reason: CommerceOrderChangeReason): CommerceOrder {
        this.listeners.emit(Object.freeze({ reason, order }));
        return order;
    }
    private requireOrderId(orderId: string): string {
        this.assertAvailable();
        const value = orderId.trim();
        if (!value) throw new CommerceError("order-id-invalid", "Order ID is required.");
        return value;
    }
}
function delay(milliseconds: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, milliseconds);
        signal.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                reject(
                    new CommerceError(
                        "order-polling-cancelled",
                        "Order status polling was cancelled.",
                    ),
                );
            },
            { once: true },
        );
    });
}
